import React, { createContext, useContext, useState, useEffect, useMemo } from 'react'
import logger from '../utils/logger'

const AuthContext = createContext(null)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null)
  const [user, setUser] = useState(null)
  const [isValidating, setIsValidating] = useState(true)

  // API base URL with fallback logic
  const API_BASE = useMemo(() => {
    const envBase = import.meta.env?.VITE_API_URL || window?.__API_URL
    if (envBase) return envBase.replace(/\/+$/g, '')

    // Dev fallback
    try {
      const u = new URL(window.location.href)
      if (['5173', '4173', '3000', '8080'].includes(u.port)) {
        return `${u.protocol}//${u.hostname}:8000`
      }
    } catch {}
    return `${location.protocol}//${location.host}`
  }, [])

  // Load token and user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token')
    const storedUser = localStorage.getItem('auth_user')

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        setToken(storedToken)
        setUser(parsedUser)

        // Verify token is still valid
        verifyToken(storedToken, parsedUser).catch(() => {
          // Token invalid, clear auth
          logout()
        })
      } catch (e) {
        logger.exception('auth', 'Failed to parse stored user', e)
        logout()
      }
    }

    setIsValidating(false)
  }, [])

  const verifyToken = async (tokenToVerify, userToVerify) => {
    try {
      const resp = await fetch(`${API_BASE}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`
        }
      })

      if (!resp.ok) {
        throw new Error('Token invalid')
      }

      return true
    } catch (e) {
      logger.exception('auth', 'Token verification failed', e)
      throw e
    }
  }

  const login = async (username, password) => {
    setIsValidating(true)
    try {
      const resp = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      })

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}))
        throw new Error(errData.error || `HTTP ${resp.status}`)
      }

      const data = await resp.json()
      const { token: newToken, user: newUser } = data

      // Store in state and localStorage
      setToken(newToken)
      setUser(newUser)
      localStorage.setItem('auth_token', newToken)
      localStorage.setItem('auth_user', JSON.stringify(newUser))

      return { success: true }
    } catch (e) {
      logger.exception('auth', 'Login failed', e)
      return { success: false, error: e.message }
    } finally {
      setIsValidating(false)
    }
  }

  const logout = () => {
    setToken(null)
    setUser(null)
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
  }

  const isAuthenticated = !!token && !!user

  const value = {
    token,
    user,
    isAuthenticated,
    isValidating,
    login,
    logout,
    API_BASE
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
